import { navigate } from 'gatsby'
import clsx from 'clsx'
import { OrderApproval, OrderCancelReason } from 'lib/graphql/storeadmin'
import { ColumnsType, Table, TableProps, Tag } from 'components/common'
import { price } from 'lib/str-formatter'
import { colors } from 'lib/colors'
import { LineOutlined } from '@ant-design/icons'

export interface Booking {
  key: string
  approval: OrderApproval
  bookingDate: string
  bookingTime: string
  cancelReason: OrderCancelReason
  createdAt: string
  customer: string
  groupName?: string | JSX.Element
  numberOfPeople: number
  orderNumber: number
  productNames?: string[]
  status: string
  total: number
  updatedAt: string
}

type InternalProps = TableProps<Booking>

type Actions = Pick<InternalProps, 'onChange' | 'onRow'>

export interface BookingsTableProps
  extends Omit<
    InternalProps,
    'sticky' | 'pagination' | 'rowClassName' | 'onChange' | 'onRow'
  > {
  dataSource: Array<Booking>
}

const columns: ColumnsType<Booking> = [
  {
    key: 'orderNumber',
    title: <div className="text-center">予約番号</div>,
    dataIndex: 'orderNumber',
    render: (value) => <div className="text-center">#{value}</div>,
  },
  {
    key: 'customer',
    title: <div className="text-center">名前</div>,
    dataIndex: 'customer',
  },
  {
    key: 'groupName',
    title: <div className="text-center">団体名</div>,
    dataIndex: 'groupName',
  },
  {
    key: 'bookingDate',
    title: <div className="text-center">利用予定日</div>,
    dataIndex: 'bookingDate',
  },
  {
    key: 'bookingTime',
    title: <div className="text-center">利用予定時間</div>,
    dataIndex: 'bookingTime',
  },
  {
    key: 'total',
    title: <div className="text-center">合計</div>,
    dataIndex: 'total',
    render: (value) => price(value),
  },
  {
    key: 'productNames',
    title: <div className="text-center">対象施設</div>,
    dataIndex: 'productNames',
    render: (values) =>
      values.map((value: string) => <div key={value}>{value}</div>),
  },
  {
    key: 'numberOfPeople',
    title: <div className="text-center">利用予定人数</div>,
    dataIndex: 'numberOfPeople',
    render: (value) => <div>{value} 人</div>,
  },
  {
    key: 'approval',
    title: <div className="text-center">承認</div>,
    dataIndex: 'approval',
    render: (value) => {
      switch (value) {
        case OrderApproval.Approved:
          return <Tag color={'success'}>承認済</Tag>
        case OrderApproval.Pending:
          return <Tag color={'warning'}>承認待ち</Tag>
        case OrderApproval.Disapproved:
        default:
          return <Tag color={'error'}>非承認</Tag>
      }
    },
  },
  {
    key: 'cancelReason',
    title: <div className="text-center">キャンセル</div>,
    dataIndex: 'cancelReason',
    render: (value) => {
      if (value) {
        return (
          <Tag style={{ textAlign: 'center' }} color={colors.YAMABUKI}>
            キャンセル済み
          </Tag>
        )
      }
      return <LineOutlined style={{ color: 'rgba(0,0,0,0.5)' }} />
    },
    width: 130,
  },
  {
    key: 'createdAt',
    title: <div className="text-center">作成日時</div>,
    dataIndex: 'createdAt',
  },
  {
    key: 'updatedAt',
    title: <div className="text-center">更新日時</div>,
    dataIndex: 'updatedAt',
  },
]

export const BookingsTable = ({
  dataSource,
  ...rest
}: BookingsTableProps): JSX.Element => {
  const onRow: Actions['onRow'] = (record) => {
    return {
      onClick: async () => {
        await navigate(`./${record.key}`)
      },
    }
  }
  return (
    <Table<Booking>
      sticky
      pagination={false}
      rowClassName={clsx('cursor-pointer text-xl text-center')}
      rowKey={(record) => record.key}
      dataSource={dataSource}
      columns={columns}
      onRow={onRow}
      {...rest}
    />
  )
}
